import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FinalGrade } from 'app/library/final-grade/models/final-grade.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { FileCommand } from 'app/projects/core/src/lib/models/file.command';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ICertificateQuery } from '../interfaces/certificate.query.interface';
import { CertificateCommand } from '../models/certificate.command.model';
import { Certificate } from '../models/certificate.model';

export interface ISimpleCertificate {
    id: string;
    name: string;
    filename?: string;
}

export interface IFinalGradeCertificate extends ISimpleCertificate {
    finalGrade?: {
        id: string;
    };
}

export interface IFinalGradeCertificateQuery extends ISimpleCertificate {
    final_grade: {
        id: string;
    };
}

export class FinalGradeCertificate {
    id: string;
    name: string;
    finalGrade?: {
        id: string;
    };

    constructor(query: IFinalGradeCertificateQuery) {
        this.id = query.id;
        this.name = query.name;
        if (query.final_grade) {
            this.finalGrade = {
                id: query.final_grade.id,
            };
        }
    }
}

@Injectable()
export class CertificateApiService extends EntityService<ICertificateQuery, CertificateCommand> implements IEntityApiService<Certificate> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'certificate');
    }

    getAll(): Observable<Certificate[]> {
        return this.http.get<ICertificateQuery[]>(`${this.apiUrl}/${this.entities}/public/templates`).pipe(map((queries) => queries.map((query) => new Certificate(query))));
    }

    uploadTemplate(payload: File): Observable<Certificate> {
        const data = new FileCommand('file', new File([payload], '', { type: 'text/plain' }), payload.name).formData;

        return this.http.post<ICertificateQuery>(`${this.apiUrl}/${this.entities}/public/templates`, data).pipe(map((query) => new Certificate(query)));
    }

    create(entity: Certificate): Observable<Certificate> {
        const data = new CertificateCommand(entity);

        return this._create(data).pipe(map((query) => new Certificate(query)));
    }

    read(id: string): Observable<Certificate> {
        throw new Error('Not implemented. ');
    }

    download({ id, name }: ISimpleCertificate): Observable<Blob> {
        const endpointUrl = `${this.apiUrl}/${this.entities}/certificates/${id}`;
        const mobileAppValue = localStorage.getItem('kmpMobileApp');

        if (mobileAppValue === 'ios' || mobileAppValue === 'android') {
            const accessToken = localStorage.getItem('accessToken');

            window.location.href = `${endpointUrl}?preview=true&access_token=${accessToken}&kmp_open_externally=true`;

            return of(new Blob());
        }

        return this.http.get(endpointUrl, { responseType: 'blob' }).pipe(tap((file) => MaxBrainUtils.downloadFile(file, name)));
    }

    assign(finalGrade: FinalGrade): Observable<FinalGradeCertificate> {
        return this.http
            .post<IFinalGradeCertificateQuery>(`${this.apiUrl}/${this.entities}/certificates`, {
                final_grade: {
                    id: finalGrade.id,
                },
            })
            .pipe(map((entity) => new FinalGradeCertificate(entity)));
    }

    unassign(finalGrade: FinalGrade): Observable<any> {
        return this.http.delete(`${this.apiUrl}/${this.entities}/certificates/${finalGrade.certificate.id}`);
    }

    bulkAssign(moduleId: string): Observable<{ id: string }> {
        return this.http.post<{ id: string }>(`${this.apiUrl}/${this.entities}/modules/${moduleId}/assign-certificate-to-all`, {});
    }

    bulkUnassign(moduleId: string): Observable<{ id: string }> {
        return this.http.delete<{ id: string }>(`${this.apiUrl}/${this.entities}/modules/${moduleId}/unassign-certificate-from-all`);
    }

    downloadTemplate({ id, name }: ISimpleCertificate): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/${this.entities}/templates/${id}`, { responseType: 'blob' }).pipe(tap((file) => MaxBrainUtils.downloadFile(file, name)));
    }

    update(entity: Certificate): Observable<Certificate> {
        const data = new CertificateCommand(entity);

        return this.http.patch<ICertificateQuery>(`${this.apiUrl}/${this.entities}/templates/${entity.id}`, data).pipe(map((query) => new Certificate(query)));
    }

    delete(id: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/${this.entities}/templates/${id}`);
    }

    fetchCertificateKeywords(): Observable<string[]> {
        return this.http.get<string[]>(`${this.apiUrl}/${this.entities}/keywords`);
    }
}
